import React, { useEffect, useState } from 'react';
import {
  Autocomplete,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  Grid,
  List,
  ListItem,
  ListItemText,
  TextField,
  Stack,
  Typography
} from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import GroupIcon from '@mui/icons-material/Group';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { ROLES } from '../constants/roles';
import { useToast } from '../contexts/ToastContext';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const RolesGlossary = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [rolesGlossary, setRolesGlossary] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUsersByRole, setSelectedUsersByRole] = useState({});
  const [grantConsentByRole, setGrantConsentByRole] = useState({});
  const [rollbackConsentByRole, setRollbackConsentByRole] = useState({});
  const [selectedRollbackUsersByRole, setSelectedRollbackUsersByRole] = useState({});
  const [submittingByRole, setSubmittingByRole] = useState({});

  const canManageRoles = [ROLES.SUPER_ADMIN, ROLES.ACCOUNTABLE_MANAGER].includes(user?.role);

  const fetchRolesGlossary = async () => {
    try {
      setLoading(true);
      const [rolesResponse, usersResponse] = await Promise.all([
        axios.get(`${API_URL}/users/roles-glossary`),
        axios.get(`${API_URL}/users/roles-glossary/users`)
      ]);
      setRolesGlossary(rolesResponse.data || []);
      setAllUsers(usersResponse.data || []);
    } catch (error) {
      console.error('Failed to load roles glossary:', error);
      showToast(error.response?.data?.message || 'Failed to load roles glossary', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!canManageRoles) {
      setLoading(false);
      return;
    }

    fetchRolesGlossary();
  }, [canManageRoles, showToast]);

  const withSubmitting = (role, value) => {
    setSubmittingByRole((prev) => ({ ...prev, [role]: value }));
  };

  const handleGivePermission = async (role) => {
    const selectedUser = selectedUsersByRole[role];
    const hasConsent = grantConsentByRole[role];

    if (!selectedUser) {
      showToast('Please select a user first.', 'warning');
      return;
    }
    if (!hasConsent) {
      showToast('Please tick the consent checkbox before submit.', 'warning');
      return;
    }

    try {
      withSubmitting(role, true);
      await axios.post(`${API_URL}/users/roles-glossary/permissions`, {
        userId: selectedUser._id,
        role
      });
      showToast('Permission granted successfully.', 'success');
      setSelectedUsersByRole((prev) => ({ ...prev, [role]: null }));
      setGrantConsentByRole((prev) => ({ ...prev, [role]: false }));
      await fetchRolesGlossary();
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to grant permission.', 'error');
    } finally {
      withSubmitting(role, false);
    }
  };

  const handleRollbackPermission = async (role) => {
    const selectedRollbackUsers = selectedRollbackUsersByRole[role] || [];
    const hasConsent = rollbackConsentByRole[role];

    if (selectedRollbackUsers.length === 0) {
      showToast('Please select at least one user to rollback.', 'warning');
      return;
    }
    if (!hasConsent) {
      showToast('Please tick rollback consent checkbox before submit.', 'warning');
      return;
    }

    try {
      withSubmitting(role, true);
      await Promise.all(
        selectedRollbackUsers.map((selectedUser) =>
          axios.post(`${API_URL}/users/roles-glossary/permissions/rollback`, { userId: selectedUser._id })
        )
      );
      showToast('Rollback completed successfully.', 'success');
      setSelectedRollbackUsersByRole((prev) => ({ ...prev, [role]: [] }));
      setRollbackConsentByRole((prev) => ({ ...prev, [role]: false }));
      await fetchRolesGlossary();
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to rollback permission.', 'error');
    } finally {
      withSubmitting(role, false);
    }
  };

  if (!canManageRoles) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Only Super Admin and Accountable Manager can access Roles Glossary.</Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading roles glossary...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} mb={3}>
        <SecurityIcon color="primary" />
        <Typography variant="h4" component="h1">
          Roles Glossary
        </Typography>
      </Stack>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Complete role reference with assigned users and access details. You can give permission and rollback role access from here.
      </Typography>

      <Grid container spacing={2}>
        {rolesGlossary.map((roleInfo) => (
          <Grid item xs={12} md={6} lg={4} key={roleInfo.role}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Stack spacing={1.5}>
                  <Typography variant="h6">{roleInfo.roleDisplayName}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {roleInfo.role}
                  </Typography>

                  <Chip
                    icon={<GroupIcon />}
                    label={`${roleInfo.userCount} user${roleInfo.userCount === 1 ? '' : 's'}`}
                    size="small"
                    color={roleInfo.userCount > 0 ? 'primary' : 'default'}
                    sx={{ alignSelf: 'flex-start' }}
                  />

                  <Divider />

                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Access Details
                    </Typography>
                    <List dense disablePadding>
                      {roleInfo.accessDetails.map((detail) => (
                        <ListItem key={detail} disableGutters sx={{ py: 0.25 }}>
                          <ListItemText primary={`- ${detail}`} />
                        </ListItem>
                      ))}
                    </List>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Give Permission
                    </Typography>
                    <Stack spacing={1}>
                      <Autocomplete
                        options={allUsers}
                        value={selectedUsersByRole[roleInfo.role] || null}
                        onChange={(_, value) => {
                          setSelectedUsersByRole((prev) => ({ ...prev, [roleInfo.role]: value }));
                        }}
                        getOptionLabel={(option) =>
                          `${option.firstName || ''} ${option.lastName || ''} (${option.email || 'No email'})`
                        }
                        isOptionEqualToValue={(option, value) => option._id === value?._id}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            label={`Search user for ${roleInfo.roleDisplayName}`}
                            placeholder="Type name or email"
                          />
                        )}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={Boolean(grantConsentByRole[roleInfo.role])}
                            onChange={(event) => {
                              const checked = event.target.checked;
                              setGrantConsentByRole((prev) => ({ ...prev, [roleInfo.role]: checked }));
                            }}
                          />
                        }
                        label={`I confirm giving ${roleInfo.roleDisplayName} permission`}
                      />
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => handleGivePermission(roleInfo.role)}
                        disabled={Boolean(submittingByRole[roleInfo.role])}
                        sx={{ alignSelf: 'flex-start' }}
                      >
                        Submit
                      </Button>
                    </Stack>
                  </Box>

                  <Divider />

                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Users in this Role
                    </Typography>
                    {roleInfo.users.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        No users assigned yet.
                      </Typography>
                    ) : (
                      <Stack spacing={0.5}>
                        {roleInfo.users.map((u) => (
                          <FormControlLabel
                            key={u._id}
                            control={
                              <Checkbox
                                checked={Boolean(
                                  (selectedRollbackUsersByRole[roleInfo.role] || []).find(
                                    (selectedUser) => selectedUser._id === u._id
                                  )
                                )}
                                onChange={(event) => {
                                  const checked = event.target.checked;
                                  setSelectedRollbackUsersByRole((prev) => {
                                    const previousUsers = prev[roleInfo.role] || [];
                                    const updatedUsers = checked
                                      ? [...previousUsers, u]
                                      : previousUsers.filter((selectedUser) => selectedUser._id !== u._id);
                                    return { ...prev, [roleInfo.role]: updatedUsers };
                                  });
                                }}
                              />
                            }
                            label={`${u.firstName} ${u.lastName} (${u.email}) ${u.isActive ? '' : '[Inactive]'}`}
                          />
                        ))}
                      </Stack>
                    )}
                    {roleInfo.users.length > 0 && (
                      <Stack spacing={1} mt={1}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={Boolean(rollbackConsentByRole[roleInfo.role])}
                              onChange={(event) => {
                                const checked = event.target.checked;
                                setRollbackConsentByRole((prev) => ({ ...prev, [roleInfo.role]: checked }));
                              }}
                            />
                          }
                          label="I confirm rollback selected users"
                        />
                        <Button
                          variant="outlined"
                          size="small"
                          color="error"
                          onClick={() => handleRollbackPermission(roleInfo.role)}
                          disabled={Boolean(submittingByRole[roleInfo.role])}
                          sx={{ alignSelf: 'flex-start' }}
                        >
                          Rollback
                        </Button>
                      </Stack>
                    )}
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default RolesGlossary;
